import { Group, Paper, Stack, Text } from "@mantine/core"
import { UserLine } from "@/components/people"
import { filterVisibleQuestions } from "@/components/surveys/stages/utils"
import { AppLoader, DateTime, NoData } from "@/components/utils"
import { useAnonymous, useAPIResources, useUsers } from "@/hooks"
import type { TSurveyAnswer, TUserSession } from "@/types"
import { answersAPI, buildUserURL, questionsAPI } from "@/utils"
import { QuestionAnswer } from "./answers"
import { UserSessionDetailsFeedback } from "./UserSessionDetailsFeedback"

export function SurveyUserSessionDetails({ usersession }: { usersession: TUserSession }) {
  const { session } = usersession
  const { data: { data: questions = [] } = {}, isPending: isQuestionsPending } = useAPIResources(questionsAPI, {
    where: { session_id: session.id },
  })
  const { data: { data: answers = [] } = {}, isPending: isAnswersPending } = useAPIResources(
    answersAPI,
    {
      where: { usersession_id: usersession.id },
    },
    session.id,
  )
  const { findUser } = useUsers()
  const anonymous = useAnonymous()
  const user = findUser(usersession.user_id) ?? anonymous
  const visibleQuestions = filterVisibleQuestions(questions, answers)

  return (
    <Stack gap="md">
      <Paper p="sm" mb="sm" withBorder>
        <Group justify="space-between" wrap="nowrap">
          <UserLine user={user} isActive={user.is_active} url={buildUserURL(user.id)} />
          <Text c="dimmed" fz="xl">
            <DateTime timestamp={usersession.started} />
          </Text>
        </Group>
      </Paper>
      {isQuestionsPending || isAnswersPending ? (
        <AppLoader />
      ) : answers.length ? (
        usersession.session.type == "feedback" ? (
          <UserSessionDetailsFeedback answers={answers} questions={questions} />
        ) : (
          visibleQuestions.map((q) => (
            <QuestionAnswer
              key={q.id}
              question={q}
              answer={answers.find((a) => a.question_id === q.id) as TSurveyAnswer}
            />
          ))
        )
      ) : (
        <NoData />
      )}
    </Stack>
  )
}
